// @ts-strict-ignore
import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Combobox,
  ComboboxGroup,
  Input,
  Radio,
  SingleDate,
  Tooltip,
} from '@node-space/ui-components'
import { FormFieldsRow } from 'components/Form/FormFieldsRow'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { CountryCodes, type ErrorDetails } from 'types/types'
import { BOOLEAN_RADIO_OPTIONS } from '../helpers/constants'
import { BUSINESS_CUSTOMER_DEFAULT_FORM_DATA } from '../helpers/form'
import {
  BusinessCustomerFormData,
  BusinessCustomerFormFields,
  ConfirmBusinessCustomerMethod,
} from '../helpers/types'
import { scrollToTopError } from '../helpers/utils'
import { useBusinessCustomerForm } from '../hooks/useBusinessCustomerForm'
import { AddCustomerActions } from './AddCustomerActions'

interface RegisteredAddressFieldsProps {
  formFields: BusinessCustomerFormFields
  formValues: Partial<BusinessCustomerFormData>
  fieldRefs: React.MutableRefObject<Record<string, HTMLElement | null>>
  t: (key: string) => string
}

const RegisteredAddressFields = ({
  formFields,
  formValues,
  fieldRefs,
  t,
}: RegisteredAddressFieldsProps) => {
  return (
    <>
      <h3 className="text-h3 text-foreground-prominent pt-2">{t('customers.registeredAddress')}</h3>
      <Box ref={el => (fieldRefs.current.addressLine1 = el)}>
        <Input {...formFields?.addressLine1} />
      </Box>
      <Box ref={el => (fieldRefs.current.addressLine2 = el)}>
        <Input {...formFields?.addressLine2} optionalText={t('optional')} />
      </Box>
      <FormFieldsRow
        leftField={
          <Box ref={el => (fieldRefs.current.city = el)}>
            <Input {...formFields?.city} />
          </Box>
        }
        rightField={
          <Box ref={el => (fieldRefs.current.postalCode = el)}>
            <Input {...formFields?.postalCode} />
          </Box>
        }
      />
      <Box ref={el => (fieldRefs.current.countryOfIncorporation = el)}>
        {formFields?.countryOfIncorporation && <Combobox {...formFields.countryOfIncorporation} />}
      </Box>
      {formValues?.countryOfIncorporation === CountryCodes.US && (
        <Box ref={el => (fieldRefs.current.stateCode = el)}>
          {formFields?.stateCode && <Combobox {...formFields.stateCode} />}
        </Box>
      )}
    </>
  )
}

interface OperationalAddressFieldsProps {
  formFields: BusinessCustomerFormFields
  formValues: Partial<BusinessCustomerFormData>
  fieldRefs: React.MutableRefObject<Record<string, HTMLElement | null>>
  t: (key: string) => string
}

const OperationalAddressFields = ({
  formFields,
  formValues,
  fieldRefs,
  t,
}: OperationalAddressFieldsProps) => {
  return (
    <>
      <h3 className="text-h3 text-foreground-prominent pt-2">
        {t('customers.operationalAddress')}
      </h3>
      <Input {...formFields.operationalAddressLine1} testId="operational-address-line1" />
      <Box ref={el => (fieldRefs.current.operationalAddressLine2 = el)}>
        <Input {...formFields?.operationalAddressLine2} optionalText={t('optional')} />
      </Box>
      <FormFieldsRow
        leftField={
          <Input {...formFields.operationalAddressCity} testId="operational-address-city" />
        }
        rightField={
          <Input
            {...formFields.operationalAddressPostalCode}
            testId="-perational-address-postal-code"
          />
        }
      />
      {formFields.operationalAddressCountryCode && (
        <Combobox
          {...formFields.operationalAddressCountryCode}
          testId="operational-address-country"
        />
      )}
      {formValues?.operationalAddressCountryCode === CountryCodes.US && (
        <Box ref={el => (fieldRefs.current.operationalAddressStateCode = el)}>
          {formFields?.operationalAddressStateCode && (
            <Combobox {...formFields.operationalAddressStateCode} />
          )}
        </Box>
      )}
    </>
  )
}

export interface AddBusinessCustomerProps {
  defaultValues: BusinessCustomerFormData
  onConfirm: ConfirmBusinessCustomerMethod
  onCancel: () => void
  fieldErrors?: ErrorDetails
  isReliance: boolean
}

export const AddBusinessCustomer = ({
  defaultValues,
  onConfirm,
  onCancel,
  fieldErrors,
  isReliance,
}: AddBusinessCustomerProps) => {
  const { t } = useTranslation()
  const { accountCapabilities } = useAccountsContext()
  const paxosUsStates = accountCapabilities?.paxosUsStatesCapability || []

  const {
    formFields,
    displayData,
    hasErrors,
    handleSubmit,
    form,
    formValues,
    setValue,
    showPaxosUsStatesFields,
    showOperationalAddress,
  } = useBusinessCustomerForm(
    defaultValues ?? BUSINESS_CUSTOMER_DEFAULT_FORM_DATA,
    fieldErrors,
    isReliance,
    paxosUsStates
  )
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({})

  const onInvalid = () => {
    scrollToTopError(form, fieldRefs)
  }

  const showUsTaxNumberField = formValues?.countryOfIncorporation === CountryCodes.US

  const registrationFields = () => {
    if (showUsTaxNumberField) {
      return (
        <Box ref={el => (fieldRefs.current.taxNumber = el)}>
          <Input {...formFields?.taxNumber} />
        </Box>
      )
    }
    return (
      <Box ref={el => (fieldRefs.current.registrationNumber = el)}>
        <Input {...formFields?.registrationNumber} />
      </Box>
    )
  }

  const handleRadioChange = (
    field: 'isRegulated' | 'isPubliclyListed',
    value: string,
    dependentFields: (keyof BusinessCustomerFormData)[]
  ) => {
    setValue(field, value as never, { shouldValidate: true })
    if (value) {
      dependentFields.forEach(dep => setValue(dep, '' as never, { shouldValidate: true }))
    }
  }

  useEffect(() => {
    if (!fieldErrors || !hasErrors) return
    scrollToTopError(form, fieldRefs)
  }, [fieldErrors, hasErrors])

  const onSubmit = (data: BusinessCustomerFormData) => {
    const confirmData = { ...data }
    const confirmDisplayData = { ...displayData }
    if (showPaxosUsStatesFields) {
      if (confirmData.isRegulated !== 'true') {
        confirmData.regulatorName = undefined
        confirmData.regulatorJurisdiction = undefined
      }
      if (confirmData.isPubliclyListed !== 'true') {
        confirmData.listedExchange = undefined
        confirmData.tickerSymbol = undefined
      }
    } else {
      confirmData.isRegulated = undefined
      confirmData.regulatorName = undefined
      confirmData.regulatorJurisdiction = undefined
      confirmData.isPubliclyListed = undefined
      confirmData.listedExchange = undefined
      confirmData.tickerSymbol = undefined
    }
    if (!showOperationalAddress) {
      confirmData.operationalAddressLine1 = undefined
      confirmData.operationalAddressLine2 = undefined
      confirmData.operationalAddressCity = undefined
      confirmData.operationalAddressPostalCode = undefined
      confirmData.operationalAddressCountryCode = undefined
      confirmData.operationalAddressStateCode = undefined
      confirmDisplayData.operationalAddressLine1 = undefined
      confirmDisplayData.operationalAddressLine2 = undefined
      confirmDisplayData.operationalAddressCity = undefined
      confirmDisplayData.operationalAddressPostalCode = undefined
      confirmDisplayData.operationalAddressCountryCode = undefined
      confirmDisplayData.operationalAddressStateCode = undefined
    }
    onConfirm({ data: confirmData, displayData: confirmDisplayData })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
      <Box flexCol gap={16}>
        <Box ref={el => (fieldRefs.current.businessName = el)}>
          <Input
            {...formFields?.businessName}
            placeholder={t('embeddedCustomers.businessFormPlaceholder.businessName')}
            testId="business-name"
          />
        </Box>
        <Box ref={el => (fieldRefs.current.businessDescription = el)}>
          <Input
            {...formFields?.businessDescription}
            testId="business-description"
            Tooltip={
              <Tooltip
                type="complex"
                bodyContent={t('embeddedCustomers.businessFormTooltip.businessDescription')}
              />
            }
          />
        </Box>
        <Box ref={el => (fieldRefs.current.industry = el)}>
          <ComboboxGroup {...formFields?.industry} />
        </Box>
        <Box ref={el => (fieldRefs.current.entityType = el)}>
          <Combobox {...formFields?.entityType} />
        </Box>
        {formFields?.businessOperationsStartDate && (
          <Box ref={el => (fieldRefs.current.businessOperationsStartDate = el)}>
            <SingleDate {...formFields.businessOperationsStartDate} />
          </Box>
        )}
        <RegisteredAddressFields
          formFields={formFields}
          formValues={formValues}
          fieldRefs={fieldRefs}
          t={t}
        />
        <Radio
          name="isOperationalAddressDifferent"
          label={t('embeddedCustomers.businessForm.isOperationalAddressDifferent')}
          options={BOOLEAN_RADIO_OPTIONS}
          value={formValues.isOperationalAddressDifferent ?? ''}
          onChange={value =>
            setValue('isOperationalAddressDifferent', value, { shouldValidate: true })
          }
          error={form.formState.errors.isOperationalAddressDifferent?.message}
          testId="-is-operational-address-different"
        />
        {showOperationalAddress && (
          <OperationalAddressFields
            formFields={formFields}
            formValues={formValues}
            fieldRefs={fieldRefs}
            t={t}
          />
        )}
        <Box ref={el => (fieldRefs.current.monthlyExpectedVolumes = el)}>
          <Combobox {...formFields?.monthlyExpectedVolumes} />
        </Box>
        <Box ref={el => (fieldRefs.current.useCase = el)}>
          <Combobox
            {...formFields?.useCase}
            Tooltip={<Tooltip type="complex" bodyContent={t('customers.serviceTypeTooltip')} />}
          />
        </Box>
        <Box ref={el => (fieldRefs.current.externalReference = el)}>
          <Input
            {...formFields?.externalReference}
            testId="business-external-reference"
            optionalText={t('optional')}
            Tooltip={
              <Tooltip type="complex" bodyContent={t('customers.externalReferenceTooltip')} />
            }
          />
        </Box>
        {registrationFields()}
        {showPaxosUsStatesFields && (
          <>
            <Box ref={el => (fieldRefs.current.isRegulated = el)}>
              <Radio
                name="isRegulated"
                label={t('embeddedCustomers.businessForm.isRegulated')}
                options={BOOLEAN_RADIO_OPTIONS}
                value={formValues?.isRegulated ?? ''}
                onChange={value =>
                  handleRadioChange('isRegulated', value, [
                    'regulatorName',
                    'regulatorJurisdiction',
                  ])
                }
                error={form?.formState?.errors?.isRegulated?.message}
                testId="is-regulated"
              />
            </Box>
            {formValues?.isRegulated === 'true' && (
              <>
                <Box ref={el => (fieldRefs.current.regulatorName = el)}>
                  <Input {...formFields?.regulatorName} testId="regulator-name" />
                </Box>
                <Box ref={el => (fieldRefs.current.regulatorJurisdiction = el)}>
                  <Combobox {...formFields?.regulatorJurisdiction} />
                </Box>
              </>
            )}
            <Box ref={el => (fieldRefs.current.isPubliclyListed = el)}>
              <Radio
                name="isPubliclyListed"
                label={t('embeddedCustomers.businessForm.isPubliclyListed')}
                options={BOOLEAN_RADIO_OPTIONS}
                value={formValues?.isPubliclyListed ?? ''}
                onChange={value =>
                  handleRadioChange('isPubliclyListed', value, ['listedExchange', 'tickerSymbol'])
                }
                error={form?.formState?.errors?.isPubliclyListed?.message}
                testId="is-publicly-listed"
              />
            </Box>
            {formValues?.isPubliclyListed === 'true' && (
              <>
                <Box ref={el => (fieldRefs.current.listedExchange = el)}>
                  <Input {...formFields?.listedExchange} testId="listed-exchange" />
                </Box>
                <Box ref={el => (fieldRefs.current.tickerSymbol = el)}>
                  <Input {...formFields?.tickerSymbol} testId="ticker-symbol" />
                </Box>
              </>
            )}
          </>
        )}
      </Box>
      <AddCustomerActions onCancel={onCancel} isDisableContinue={hasErrors} />
    </form>
  )
}
